import {
  ADMIN_LOCATIONS_V_DRIVE,
  ADMIN_LOCATIONS_V_DRIVE_ASSIGNMENT_STRATEGY,
  ImproveWithAILocation,
  replaceRouteParams,
  VDriveStrategySection,
} from '@vgrubs/components';
import { useNavigate } from 'react-router-dom';
import {
  StrategyRouteState,
  StrategySuggestionModal,
  useStrategySuggestion,
} from '../../../VDrive/AssignmentStrategy/aiSuggestion';

type Props = {
  location: ImproveWithAILocation;
  isOpen: boolean;
  closeModal: () => void;
};

/**
 * "Improve with AI" for one of the Worst Performing Locations: the same AI
 * strategy suggestion as on the location's strategy screen, started from
 * here.
 *
 * Mounted per location (keyed on its id) and kept mounted while the modal is
 * closed, so the run goes on polling and reopening the row shows it. The job
 * id is kept per location for the tab, so the strategy screen picks up this
 * very job too.
 *
 * Apply cannot fill a form here, so it opens the strategy screen with the
 * ticked sections, which applies them there (see AssignmentStrategy). The
 * vDrive page is put in history under it on purpose: the strategy screen's
 * Confirm goes back one step, and it is the vDrive page's Save that stores
 * the strategy - back on the dashboard the changes would just be lost.
 */
export const LocationStrategySuggestion = ({
  location,
  isOpen,
  closeModal,
}: Props) => {
  const navigate = useNavigate();
  const id = String(location.id);
  const suggestion = useStrategySuggestion({ scope: 'location', id });

  const handleApply = (sections: VDriveStrategySection[]) => {
    closeModal();
    const state: StrategyRouteState = {
      aiSections: sections.map((section) => section.key),
    };
    navigate(replaceRouteParams(ADMIN_LOCATIONS_V_DRIVE, { id }));
    navigate(
      replaceRouteParams(ADMIN_LOCATIONS_V_DRIVE_ASSIGNMENT_STRATEGY, { id }),
      { state }
    );
  };

  return (
    <StrategySuggestionModal
      isOpen={isOpen}
      closeModal={closeModal}
      scopeLabel="location"
      name={location.name}
      appliesOnStrategyScreen
      // Above the list's own "see all" modal, which it can be opened from.
      zIndex={140}
      status={suggestion.status}
      result={suggestion.result}
      errorMessage={suggestion.errorMessage}
      onStart={suggestion.start}
      onReset={suggestion.reset}
      onApply={handleApply}
    />
  );
};
